import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cookie, Settings2 } from "lucide-react";
import { clearConsent } from "@/utils/consent";

export default function CookiePolicyPage() {
  const onResetConsent = () => {
    clearConsent();
    // Reload so the banner appears again
    setTimeout(() => window.location.reload(), 50);
  };

  return (
    <main className="min-h-screen" style={{ background: "var(--np-quaternary)" }}>
      {/* Top bar */}
      <div className="border-b" style={{ borderColor: "rgba(26,42,58,0.08)" }}>
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline underline-offset-4"
            style={{ color: "var(--np-primary)" }}
          >
            <ArrowLeft size={16} />
            Torna alla home
          </Link>
          <button
            type="button"
            onClick={onResetConsent}
            data-testid="cookie-policy-reset-consent"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold border transition hover:bg-white"
            style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}
          >
            <Settings2 size={14} />
            Modifica preferenze cookie
          </button>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: "#ffffff" }}
          >
            <Cookie size={22} style={{ color: "var(--np-secondary)" }} />
          </div>
          <p className="text-[12px] font-semibold tracking-[0.2em] uppercase" style={{ color: "var(--np-secondary)" }}>
            Informativa
          </p>
        </div>

        <h1
          className="text-4xl md:text-5xl leading-tight tracking-tight"
          style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}
        >
          Cookie Policy
        </h1>
        <p className="mt-3 text-sm" style={{ color: "var(--np-muted)" }}>
          Ultimo aggiornamento: 21 febbraio 2026
        </p>

        <div className="prose-np mt-10 space-y-8 text-[16px] leading-[1.75]" style={{ color: "#2a3a4a" }}>
          <Section title="1. Titolare del trattamento">
            <p>
              Titolare del trattamento è <strong>BeeToo Agency S.r.l.</strong>, P. IVA{" "}
              <strong>01275560322</strong>. Per qualsiasi richiesta in materia di privacy o cookie puoi contattarci
              all&apos;indirizzo{" "}
              <a href="mailto:info@beetooagency.com" className="font-medium underline-offset-4 hover:underline" style={{ color: "var(--np-primary)" }}>
                info@beetooagency.com
              </a>
              .
            </p>
          </Section>

          <Section title="2. Cosa sono i cookie">
            <p>
              I cookie sono piccoli file di testo che i siti visitati inviano al browser dell&apos;utente, dove vengono
              memorizzati per essere ritrasmessi agli stessi siti alla visita successiva. Possono essere usati anche
              identificatori simili (es. pixel, localStorage). Questo sito utilizza sia cookie tecnici sia, previo
              consenso, cookie di profilazione/marketing di terze parti.
            </p>
          </Section>

          <Section title="3. Tipologie di cookie utilizzati">
            <p>
              <strong>Cookie tecnici (necessari)</strong> — indispensabili per il corretto funzionamento del sito (es.
              mantenimento della sessione, memorizzazione della preferenza di consenso). Non richiedono consenso.
            </p>
            <p>
              <strong>Cookie di marketing (Meta Pixel + Pubblici Personalizzati)</strong> — installati solo dopo il tuo consenso esplicito.
              Utilizziamo il Meta Pixel di Facebook/Instagram (ID Pixel <code>1817378319234579</code>) e la Conversions
              API (CAPI) lato server, gestiti da Meta Platforms Ireland Ltd, per misurare l&apos;efficacia delle nostre
              campagne pubblicitarie e mostrare annunci pertinenti su Facebook e Instagram. I dati raccolti
              comprendono, in forma hashata (SHA-256), email, telefono, nome, oltre a indirizzo IP, user agent e
              identificatori dei cookie <code>_fbp</code> e <code>_fbc</code>.
            </p>
            <p>
              <strong>Pubblici Personalizzati (Custom Audiences) Meta per retargeting</strong> — i dati hashati che inviamo a Meta vengono
              utilizzati anche per costruire <em>pubblici personalizzati</em> di Facebook/Instagram (es. &laquo;visitatori
              della landing&raquo;, &laquo;lead non convertiti&raquo;, &laquo;acquirenti Lezione 2&raquo;) e <em>pubblici simili (lookalike)</em>,
              al solo scopo di mostrarti annunci più pertinenti o escluderti da quelli che hai già visto. Il trattamento
              avviene esclusivamente in presenza di consenso ai cookie di marketing e su base giuridica del consenso
              esplicito (art. 6.1.a GDPR). Puoi opporti in qualsiasi momento revocando il consenso ai cookie di marketing
              da questa pagina.
            </p>
            <p>
              <strong>Cookie di analisi statistica (Google Analytics 4)</strong> — installati solo dopo il tuo consenso esplicito.
              Utilizziamo Google Analytics 4 (proprietà <code>G-9N6NRQC3NK</code>) e il relativo Measurement Protocol
              server-side, gestiti da Google Ireland Limited, per misurare il traffico, i percorsi di navigazione e
              l&apos;efficacia delle pagine. I dati raccolti includono identificatori pseudonimi (<code>_ga</code>,
              <code>_ga_*</code>), indirizzo IP (con anonimizzazione attiva), pagine visitate, durata della sessione
              e eventi standard (<code>generate_lead</code>, <code>sign_up</code>, <code>purchase</code>).
              Adottiamo il <strong>Consent Mode v2</strong>: i cookie e gli identificatori sono di default disabilitati
              e si attivano SOLO al tuo consenso esplicito. È attivo l&apos;<em>IP anonymization</em>.
            </p>
            <p>
              <strong>Cookie di marketing/remarketing (Google Ads + Pubblici Personalizzati)</strong> — installati solo dopo il tuo consenso esplicito.
              Utilizziamo Google Ads (ID conversione <code>AW-18250952995</code>), gestito da Google Ireland Limited, per
              misurare le conversioni e mostrare annunci di <em>remarketing</em> sulla rete Google (Search, Display, YouTube,
              Gmail) agli utenti che hanno visitato il sito. Costruiamo <em>elenchi di pubblico personalizzati</em> (es.
              &laquo;visitatori landing&raquo;, &laquo;hanno iniziato il checkout&raquo;, &laquo;acquirenti corso completo&raquo;) ed eventualmente
              <em>pubblici simili</em> per ottimizzare la copertura. I dati raccolti includono identificatori dei cookie Google
              (<code>_gcl_au</code>, <code>_gcl_aw</code>) e l&apos;IP. Anche per Google Ads è attivo il Consent Mode v2:
              senza il tuo consenso esplicito ai cookie di marketing, Google riceve solo segnali pseudonimi/anonimizzati
              che non permettono di profilarti.
            </p>
          </Section>

          <Section title="4. Tabella riepilogativa">
            <div className="overflow-x-auto rounded-xl border border-[#e3eaf2] bg-white">
              <table className="w-full text-sm">
                <thead className="text-left" style={{ background: "var(--np-tertiary)" }}>
                  <tr>
                    <th className="px-4 py-3 font-semibold" style={{ color: "var(--np-primary)" }}>Nome</th>
                    <th className="px-4 py-3 font-semibold" style={{ color: "var(--np-primary)" }}>Fornitore</th>
                    <th className="px-4 py-3 font-semibold" style={{ color: "var(--np-primary)" }}>Categoria</th>
                    <th className="px-4 py-3 font-semibold" style={{ color: "var(--np-primary)" }}>Durata</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#eef2f7]">
                  <tr>
                    <td className="px-4 py-3"><code>np_cookie_consent_v1</code></td>
                    <td className="px-4 py-3">beetooagency.com</td>
                    <td className="px-4 py-3">Tecnico</td>
                    <td className="px-4 py-3">12 mesi</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><code>_fbp</code></td>
                    <td className="px-4 py-3">facebook.com</td>
                    <td className="px-4 py-3">Marketing</td>
                    <td className="px-4 py-3">3 mesi</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><code>_fbc</code></td>
                    <td className="px-4 py-3">facebook.com</td>
                    <td className="px-4 py-3">Marketing</td>
                    <td className="px-4 py-3">2 anni</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><code>_ga</code></td>
                    <td className="px-4 py-3">google.com</td>
                    <td className="px-4 py-3">Analisi statistica</td>
                    <td className="px-4 py-3">2 anni</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><code>_ga_9N6NRQC3NK</code></td>
                    <td className="px-4 py-3">google.com</td>
                    <td className="px-4 py-3">Analisi statistica</td>
                    <td className="px-4 py-3">2 anni</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><code>_gcl_au</code></td>
                    <td className="px-4 py-3">google.com</td>
                    <td className="px-4 py-3">Marketing (Google Ads)</td>
                    <td className="px-4 py-3">3 mesi</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><code>_gcl_aw</code></td>
                    <td className="px-4 py-3">google.com</td>
                    <td className="px-4 py-3">Marketing (Google Ads)</td>
                    <td className="px-4 py-3">3 mesi</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="5. Conversions API e Pubblici Personalizzati (eventi server-side)">
            <p>
              Per misurare le conversioni in modo affidabile inviamo a Meta anche eventi server-to-server (Conversions
              API) quando ti registri, completi la registrazione, raggiungi tappe del corso o acquisti. Tali eventi includono
              il tuo evento (es. <em>Lead</em>, <em>CompleteRegistration</em>, <em>QualifiedLead</em>, <em>Purchase</em>) con
              un identificativo univoco e i tuoi dati personali in <strong>forma hashata (SHA-256)</strong>: email, telefono,
              nome, oltre a indirizzo IP, user agent e identificatori cookie <code>_fbp</code> e <code>_fbc</code>.
              Questi eventi alimentano sia la misurazione delle campagne (deduplicata col Pixel via <em>event_id</em>) sia
              la costruzione dei <strong>Pubblici Personalizzati (Custom Audiences)</strong> Meta per il retargeting.
              La base giuridica è il tuo consenso esplicito ai cookie di marketing (art. 6.1.a GDPR) — senza consenso, gli
              eventi non vengono inviati.
            </p>
          </Section>

          <Section title="6. Come gestire il consenso">
            <p>
              Al primo accesso ti viene mostrato un banner che ti permette di accettare, rifiutare o personalizzare le
              tue scelte. Puoi modificarle in qualsiasi momento usando il pulsante{" "}
              <strong>&laquo;Modifica preferenze cookie&raquo;</strong> in questa pagina oppure cancellando i cookie
              del tuo browser.
            </p>
          </Section>

          <Section title="7. Diritti dell'interessato">
            <p>
              In conformità al Regolamento UE 2016/679 (GDPR) hai diritto di accedere, rettificare, cancellare,
              limitare od opporti al trattamento dei tuoi dati, nonché di proporre reclamo all&apos;Autorità Garante.
              Per esercitare i tuoi diritti scrivi a{" "}
              <a href="mailto:info@beetooagency.com" className="font-medium underline-offset-4 hover:underline" style={{ color: "var(--np-primary)" }}>
                info@beetooagency.com
              </a>
              .
            </p>
          </Section>
        </div>
      </article>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2
        className="text-xl md:text-2xl mb-3"
        style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}
      >
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
